import { m } from '@/i18n';
import { mayAct } from '../access';
import { domainError } from '../types';
import { answerFor } from './clarification';
import { GO_AHEAD_ANSWER, LEAVE_IT_ANSWER } from './policies';
import { questionId } from './stimulus';
import type { Context, Question, QuestionClass, Resolution, Stimulus } from './types';

/** May the context's actor act on its item at all (`mayAct`, or the standing
 *  the read layer already graded)? The one question step 5 asks first, also
 *  read by a surface that shows an act only to whoever may take it. */
export function contextMayAct(context: Context): boolean {
  const { item, access } = context;
  return access.standing !== undefined
    ? access.standing === 'act'
    : mayAct(item.visibility, access.role, access.orgKind, access.memberOfAnyGroup, access.own);
}

/**
 * **Step 5: permissions, after the intent, never before**
 * (docs/RESOLUTION.md §1). Meaning does not depend on rights; rights decide
 * whether the meaning may be executed. A missing right is a LANE (a proposal,
 * or an access request), not a dead end, and an out-of-role step is neither: it
 * is flagged, and only a `critical` level asks, and only where someone waits.
 */

/**
 * **Step 5: permissions, after the intent, never before.** Meaning does not
 * depend on rights; rights decide whether the meaning may be executed. A
 * missing right is a LANE, not a dead end:
 *
 * - no right to act on the item at all (`mayAct` - the Read rung: an explicit
 *   view-only path sees and does not act) → **request access**;
 * - a step the actor may not take although they may act here (today: replacing
 *   the requester seat, the one reserved act) → **propose**, carrying the same
 *   intent to the owner;
 * - an out-of-role step is neither: it is FLAGGED, and its level rides the
 *   step (Prinzip 20). Only a `critical` level asks, and only where someone
 *   waits (§3).
 */
export function withPermissions(
  stimulus: Stimulus,
  context: Context,
  resolution: Resolution,
): Resolution {
  if (resolution.kind !== 'plan') return resolution;
  const plan = resolution.plan;
  const { access } = context;

  if (!contextMayAct(context)) {
    return {
      kind: 'lane',
      lane: 'request-access',
      intent: plan.intent,
      reason: m('resolution.lane.viewOnly'),
    };
  }

  const reserved = plan.steps.find(
    (s) => s.command.type === 'Reassign' && s.command.role === 'requester',
  );
  if (reserved && !access.ownerReach) {
    return {
      kind: 'lane',
      lane: 'propose',
      intent: plan.intent,
      reason: m('resolution.lane.ownerReach'),
    };
  }

  // A sweep reaches items of its own; a child the actor may not act on was
  // skipped rather than planned (§6), so nothing here can smuggle one in.
  const critical = plan.steps.some((s) => s.level === 'critical');
  if (!critical || plan.confirm) return resolution;

  const klass: QuestionClass = 'out-of-role';
  const question: Question = {
    id: questionId(stimulus.key, klass),
    kind: 'confirmation',
    class: klass,
    text: m('resolution.question.outOfRole'),
    options: [
      { key: GO_AHEAD_ANSWER, label: m('resolution.answer.goAhead') },
      { key: LEAVE_IT_ANSWER, label: m('resolution.answer.leaveIt') },
    ],
    answerers: [context.actor.ref],
  };
  const answered = answerFor(question, context);
  if (answered.kind === 'unanswered') {
    const out = answered.resolution(plan.intent);
    // Interactive and agentic: the confirmation RIDES the plan (§6), so the
    // surface shows what will happen next to the question. Async's policy
    // lanes and findings pass through as they are.
    if (out.kind === 'question') {
      return { kind: 'plan', plan: { ...plan, confirm: question } };
    }
    return out;
  }
  if (answered.answer.optionKey !== GO_AHEAD_ANSWER) {
    return {
      kind: 'refused',
      intent: plan.intent,
      error: domainError('no_change', m('resolution.refusal.leftAlone')),
    };
  }
  return resolution;
}
